import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  PAYROLL_VOUCHER_STATUS_LABELS,
  PayrollVoucherStatusEnum,
} from "@/enum/payroll-voucher-status";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { formatToBRL } from "@/utils/formatToMoney";
import { CheckCircle, XCircle } from "lucide-react";
import { PAYROLL_VOUCHER_RESPONSE_LABELS } from "../../utils/messages";
import {
  PayrollVoucherInstallmentSummary,
  PayrollVoucherSummary,
} from "./payroll-voucher-types";

interface PayrollVoucherResponseModalProps {
  voucher: PayrollVoucherSummary;
}

const formatDecidedAt = (value: string | null): string => {
  if (!value) return "-";

  const date: Date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return `${date.toLocaleDateString("pt-BR")} às ${date.toLocaleTimeString(
    "pt-BR",
    { hour: "2-digit", minute: "2-digit" }
  )}`;
};

export function PayrollVoucherResponseModal({
  voucher,
}: PayrollVoucherResponseModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const approved: boolean =
    voucher.status === PayrollVoucherStatusEnum.APPROVED;
  const installments: PayrollVoucherInstallmentSummary[] =
    voucher.installments ?? [];

  const total: number = installments.reduce(
    (accumulated: number, installment: PayrollVoucherInstallmentSummary) =>
      accumulated + Number(installment.amount ?? 0),
    0
  );

  return (
    <div className="mx-auto w-full max-w-md space-y-4">
      <Badge variant={approved ? "approved" : "rejected"}>
        {approved ? (
          <CheckCircle className="mr-1 h-3 w-3" />
        ) : (
          <XCircle className="mr-1 h-3 w-3" />
        )}
        {PAYROLL_VOUCHER_STATUS_LABELS[voucher.status]}
      </Badge>

      <div className="space-y-1 text-sm">
        <div className="flex gap-2">
          <span className="text-gray-500">
            {PAYROLL_VOUCHER_RESPONSE_LABELS.DECIDED_BY}
          </span>
          <span className="font-medium text-gray-900">
            {voucher.decided_by_email || "-"}
          </span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-500">
            {PAYROLL_VOUCHER_RESPONSE_LABELS.DECIDED_AT}
          </span>
          <span className="font-medium text-gray-900">
            {formatDecidedAt(voucher.decided_at)}
          </span>
        </div>
      </div>

      {approved && installments.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">
            {PAYROLL_VOUCHER_RESPONSE_LABELS.INSTALLMENTS}
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="py-2 font-medium">
                  {PAYROLL_VOUCHER_RESPONSE_LABELS.INSTALLMENT}
                </th>
                <th className="py-2 text-right font-medium">
                  {PAYROLL_VOUCHER_RESPONSE_LABELS.AMOUNT}
                </th>
              </tr>
            </thead>
            <tbody>
              {installments.map(
                (installment: PayrollVoucherInstallmentSummary) => (
                  <tr
                    key={installment.number}
                    className="border-b border-gray-100"
                  >
                    <td className="py-2 text-gray-700">
                      {installment.number}/{installments.length}
                    </td>
                    <td className="py-2 text-right text-gray-900">
                      {formatToBRL(installment.amount)}
                    </td>
                  </tr>
                )
              )}
              <tr>
                <td className="py-2 font-medium text-gray-900">
                  {PAYROLL_VOUCHER_RESPONSE_LABELS.TOTAL}
                </td>
                <td className="py-2 text-right font-medium text-gray-900">
                  {formatToBRL(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!approved && voucher.rejection_reason && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">
            {PAYROLL_VOUCHER_RESPONSE_LABELS.REJECTION_REASON}
          </p>
          <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
            {voucher.rejection_reason}
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="outline" onClick={() => handleModal({ isOpen: false })}>
          {PAYROLL_VOUCHER_RESPONSE_LABELS.CLOSE}
        </Button>
      </div>
    </div>
  );
}
